import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  AssignmentOutlined,
  AnalyticsOutlined,
  CompareArrowsOutlined,
  InventoryOutlined,
  MonetizationOnOutlined,
  RecommendOutlined,
  CheckCircleOutlined
} from '@mui/icons-material';

interface NavigationSection {
  id: string;
  label: string;
  icon: React.ReactNode;
  subsections?: Array<{ id: string; label: string }>;
}

interface Props {
  activeSection: string;
  onSectionClick: (sectionId: string) => void;
  completedSections: string[];
}

const navigationSections: NavigationSection[] = [
  {
    id: 'executive-summary',
    label: 'Executive Summary',
    icon: <AssignmentOutlined />
  },
  {
    id: 'market-overview',
    label: 'Market Overview',
    icon: <AnalyticsOutlined />
  },
  {
    id: 'competitive-analysis',
    label: 'Competitive Analysis',
    icon: <CompareArrowsOutlined />
  },
  {
    id: 'supply-analysis',
    label: 'Supply Analysis',
    icon: <InventoryOutlined />
  },
  {
    id: 'economic-indicators',
    label: 'Economic Indicators',
    icon: <MonetizationOnOutlined />
  },
  {
    id: 'recommendations',
    label: 'Strategic Recommendations',
    icon: <RecommendOutlined />
  }
];

export const NavigationSidebar: React.FC<Props> = ({
  activeSection,
  onSectionClick,
  completedSections = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSectionClick = (sectionId: string) => {
    onSectionClick(sectionId);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isCompleted = (sectionId: string) => completedSections.includes(sectionId);
  const isActive = (sectionId: string) => activeSection === sectionId;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '0.9rem' }}>
            Report Navigation
          </Typography>
          {isMobile && (
            <IconButton onClick={handleDrawerToggle} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
          Market Research Analysis
        </Typography>
      </Box>

      {/* Progress Overview */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Completion Progress
          </Typography>
          <Chip
            label={`${completedSections.length}/${navigationSections.length}`}
            size="small"
            color={completedSections.length === navigationSections.length ? 'success' : 'primary'}
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>
        <Box
          sx={{
            width: '100%',
            height: 3,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              width: `${(completedSections.length / navigationSections.length) * 100}%`,
              height: '100%',
              backgroundColor: theme.palette.primary.main,
              transition: 'width 0.3s ease'
            }}
          />
        </Box>
      </Box>

      {/* Navigation List */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        py: 0.5,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: alpha(theme.palette.divider, 0.1),
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.divider, 0.3),
          borderRadius: '3px',
          '&:hover': {
            background: alpha(theme.palette.divider, 0.5),
          },
        },
      }}>
        <List disablePadding>
          {navigationSections.map((section) => (
            <React.Fragment key={section.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleSectionClick(section.id)}
                  sx={{
                    py: 1,
                    px: 1.5,
                    backgroundColor: isActive(section.id) ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    borderLeft: isActive(section.id) ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: isActive(section.id) ? theme.palette.primary.main : theme.palette.text.secondary }}>
                    {section.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={section.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontSize: '0.8rem',
                      fontWeight: isActive(section.id) ? 600 : 500,
                      color: isActive(section.id) ? theme.palette.primary.main : theme.palette.text.primary
                    }}
                  />
                  {isCompleted(section.id) && (
                    <CheckCircleOutlined sx={{ fontSize: 14, color: theme.palette.success.main, ml: 0.5 }} />
                  )}
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Footer Actions */}
      <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label="Print"
            size="small"
            variant="outlined"
            onClick={() => window.print()}
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
          <Chip
            label="Export"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 80,
            left: 16,
            zIndex: 1200,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[4],
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1)
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Desktop Drawer */}
      {!isMobile && (
        <Box
          sx={{
            width: 240,
            flexShrink: 0,
            position: 'sticky',
            top: 16, // Add some top margin
            height: 'calc(100vh - 32px)', // Adjust height for margin
            alignSelf: 'flex-start',
            '& > div': {
              borderRight: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              height: '100%',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {drawerContent}
          </Box>
        </Box>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 240,
              boxSizing: 'border-box'
            }
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};